import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  makeStyles,
} from '@fluentui/react-components';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';

import {
  MIN_SEARCH_LENGTH,
  extractConsentError,
  getGroupDevices,
  isGraphError,
  searchSourceGroups,
  sourceGroupErrorCode,
} from '../api/sourceGroups';
import { ApiError } from '../api/client';
import {
  GroupDevice,
  GroupDevicesResponse,
  SourceGroup,
  SourceGroupsResponse,
  UnresolvedGroupDevice,
} from '../types/sourceGroups';
import { DataTable, DataTableColumn } from './DataTable';
import RelativeTime from './RelativeTime';
import ConsentMessageBar from './identity/ConsentMessageBar';

const DEBOUNCE_MS = 300;

export interface SourceGroupPickerProps {
  migrationId: string;
  // The serials the admin has staged for assignment — shared with the lookup
  // and paste tabs (ONE unified staged set, one footer submit). Selecting a
  // resolved device toggles its serial here; the whole group's resolved
  // devices are staged by default when it is expanded.
  selectedSerials: Set<string>;
  onToggleSerial: (serial: string) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  hint: {
    fontSize: '12px',
    color: '#52525b',
  },
  surface: {
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    borderBottomLeftRadius: '6px',
    borderBottomRightRadius: '6px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
});

// "Select group" body of the T2T assign flow (FD-113) — the operator's
// PREFERRED device-selection path. Master→detail: search the migration's
// SOURCE tenant Entra/Intune groups by name → pick the group they already
// target the Flight Agent deployment with → expand it to its DEVICE members
// (correlated server-side to Intune serials) → the resolved serials stage into
// the SAME unified set the lookup/paste tabs feed. `unresolvedDevices[]`
// (device members with no Intune managedDevice) are surfaced as a separate
// non-selectable section — never hidden. Feeds the unchanged add-devices call.
export default function SourceGroupPicker({
  migrationId,
  selectedSerials,
  onToggleSerial,
  disabled,
}: SourceGroupPickerProps) {
  const [selectedGroup, setSelectedGroup] = useState<SourceGroup | null>(null);

  if (selectedGroup) {
    return (
      <GroupDeviceExpansion
        migrationId={migrationId}
        group={selectedGroup}
        selectedSerials={selectedSerials}
        onToggleSerial={onToggleSerial}
        onChangeGroup={() => setSelectedGroup(null)}
        disabled={disabled}
      />
    );
  }

  return (
    <GroupSearch
      migrationId={migrationId}
      onSelectGroup={setSelectedGroup}
      disabled={disabled}
    />
  );
}

// --- Group search (master view) ----------------------------------------------

function GroupSearch({
  migrationId,
  onSelectGroup,
  disabled,
}: {
  migrationId: string;
  onSelectGroup: (group: SourceGroup) => void;
  disabled?: boolean;
}) {
  const styles = useStyles();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const searchReady = search.length >= MIN_SEARCH_LENGTH;

  const query = useInfiniteQuery<SourceGroupsResponse, Error>({
    queryKey: ['source-groups', migrationId, search],
    queryFn: ({ pageParam }) =>
      searchSourceGroups(migrationId, {
        search,
        skipToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.skipToken ?? undefined,
    enabled: searchReady,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 409 || error.status === 400)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const groups = useMemo<SourceGroup[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.groups) : []),
    [query.data],
  );

  const columns = useMemo<DataTableColumn<SourceGroup>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Group',
        render: (g) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{g.displayName ?? '—'}</span>
            {g.description ? (
              <span className="text-xs text-zinc-500">{g.description}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'membershipType',
        header: 'Membership',
        render: (g) => (
          <Badge appearance="tint" color={g.membershipType === 'dynamic' ? 'informative' : 'subtle'}>
            {g.membershipType === 'dynamic' ? 'Dynamic' : 'Assigned'}
          </Badge>
        ),
      },
      {
        key: 'kind',
        header: 'Type',
        render: (g) => (
          <span className="text-zinc-600">
            {[g.securityEnabled ? 'Security' : null, g.mailEnabled ? 'Mail' : null]
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        ),
      },
      {
        key: 'drill',
        header: '',
        align: 'right',
        render: () => <ChevronRight size={16} strokeWidth={1.5} className="text-zinc-400" />,
      },
    ],
    [],
  );

  const consent = query.isError ? extractConsentError(query.error) : null;
  const errorNode = query.isError
    ? consent
      ? <ConsentMessageBar consent={consent} />
      : <GroupErrorBar error={query.error} onRetry={() => query.refetch()} />
    : undefined;

  return (
    <div className={styles.body}>
      <div className={styles.hint}>
        Search the source tenant for the Entra / Intune group you target with the Flight
        Agent deployment, then bring in the devices it contains.
      </div>
      <Field>
        <Input
          value={searchInput}
          onChange={(_, data) => setSearchInput(data.value)}
          placeholder="Search groups by name"
          contentBefore={<Search size={14} strokeWidth={1.5} />}
          aria-label="Search source-tenant groups by name"
          disabled={disabled}
        />
      </Field>
      {!searchReady ? (
        <div className={styles.hint}>
          Type at least {MIN_SEARCH_LENGTH} characters of a group name to search the source
          tenant.
        </div>
      ) : (
        <>
          <div className={styles.surface}>
            <DataTable
              columns={columns}
              data={groups}
              keyForRow={(g) => g.id}
              isLoading={query.isLoading}
              loadingRowCount={4}
              error={errorNode}
              onRowClick={(g) => onSelectGroup(g)}
              emptyState={
                <span className={styles.hint}>
                  No group matching “{search}” in the source tenant.
                </span>
              }
            />
          </div>
          {query.hasNextPage && !query.isError && (
            <div className={styles.loadMoreRow}>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Group device expansion (detail view) ------------------------------------

function GroupDeviceExpansion({
  migrationId,
  group,
  selectedSerials,
  onToggleSerial,
  onChangeGroup,
  disabled,
}: {
  migrationId: string;
  group: SourceGroup;
  selectedSerials: Set<string>;
  onToggleSerial: (serial: string) => void;
  onChangeGroup: () => void;
  disabled?: boolean;
}) {
  const styles = useStyles();
  const [transitive, setTransitive] = useState(false);

  const query = useInfiniteQuery<GroupDevicesResponse, Error>({
    queryKey: ['source-group-devices', migrationId, group.id, transitive],
    queryFn: ({ pageParam }) =>
      getGroupDevices(migrationId, group.id, {
        transitive,
        skipToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.skipToken ?? undefined,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 409 || error.status === 400)) {
        return false;
      }
      // A missing group (404 GROUP_NOT_FOUND) is definitive — do not retry.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });

  const devices = useMemo<GroupDevice[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.devices) : []),
    [query.data],
  );
  const unresolved = useMemo<UnresolvedGroupDevice[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.unresolvedDevices) : []),
    [query.data],
  );

  // Default: every resolved device in the group is staged. Track which serials
  // we have already auto-staged (per group + transitivity) so a deliberate
  // deselect is never undone, and newly-paged devices are still defaulted-in.
  const autoStagedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    autoStagedRef.current = new Set();
  }, [group.id, transitive]);

  useEffect(() => {
    for (const d of devices) {
      const serial = d.serialNumber;
      if (!serial) continue;
      if (autoStagedRef.current.has(serial)) continue;
      autoStagedRef.current.add(serial);
      if (!selectedSerials.has(serial)) onToggleSerial(serial);
    }
  }, [devices, selectedSerials, onToggleSerial]);

  const columns = useMemo<DataTableColumn<GroupDevice>[]>(
    () => [
      {
        key: 'deviceName',
        header: 'Device',
        render: (d) => (
          <span className="font-medium text-zinc-900">
            {d.deviceName ?? d.entraDeviceDisplayName ?? '—'}
          </span>
        ),
      },
      {
        key: 'serialNumber',
        header: 'Serial',
        numeric: true,
        render: (d) => <span>{d.serialNumber ?? '—'}</span>,
      },
      {
        key: 'os',
        header: 'OS',
        render: (d) => (
          <span>
            {d.operatingSystem ?? '—'}
            {d.osVersion ? ` ${d.osVersion}` : ''}
          </span>
        ),
      },
      {
        key: 'model',
        header: 'Model',
        render: (d) => (
          <span>{[d.manufacturer, d.model].filter(Boolean).join(' ') || '—'}</span>
        ),
      },
      {
        key: 'complianceState',
        header: 'Compliance',
        render: (d) =>
          d.complianceState ? (
            <Badge
              appearance="tint"
              color={d.complianceState.toLowerCase() === 'compliant' ? 'success' : 'warning'}
            >
              {d.complianceState}
            </Badge>
          ) : (
            <span className="text-zinc-400">—</span>
          ),
      },
      {
        key: 'userPrincipalName',
        header: 'Enrolled user',
        render: (d) => <span>{d.userPrincipalName ?? '—'}</span>,
      },
      {
        key: 'lastSyncDateTime',
        header: 'Last sync',
        numeric: true,
        render: (d) => <RelativeTime iso={d.lastSyncDateTime} />,
      },
    ],
    [],
  );

  const selection = useMemo(
    () => ({
      selectedKeys: selectedSerials,
      onSelectionChange: (next: Set<string>) => {
        const added = [...next].filter((s) => !selectedSerials.has(s));
        const removed = [...selectedSerials].filter((s) => !next.has(s));
        for (const s of [...added, ...removed]) onToggleSerial(s);
      },
      keyForSelection: (d: GroupDevice) => d.serialNumber ?? d.id,
    }),
    [selectedSerials, onToggleSerial],
  );

  const consent = query.isError ? extractConsentError(query.error) : null;
  const errorNode = query.isError
    ? consent
      ? <ConsentMessageBar consent={consent} />
      : <GroupErrorBar error={query.error} onRetry={() => query.refetch()} />
    : undefined;

  const stagedInGroup = devices.filter(
    (d) => d.serialNumber && selectedSerials.has(d.serialNumber),
  ).length;

  return (
    <div className={styles.body}>
      <div className={styles.detailHeader}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronLeft size={14} strokeWidth={1.5} />}
          onClick={onChangeGroup}
          disabled={disabled}
        >
          Change group
        </Button>
        <span className="inline-flex items-center gap-1.5 font-medium text-zinc-900">
          <Users size={14} strokeWidth={1.5} className="text-zinc-500" />
          {group.displayName ?? '—'}
        </span>
        <Badge appearance="tint" color={group.membershipType === 'dynamic' ? 'informative' : 'subtle'}>
          {group.membershipType === 'dynamic' ? 'Dynamic' : 'Assigned'}
        </Badge>
      </div>

      {group.membershipType === 'dynamic' && group.membershipRule ? (
        <div className={styles.hint}>
          Dynamic rule: <span className="font-mono text-xs">{group.membershipRule}</span>
        </div>
      ) : null}

      <Switch
        checked={transitive}
        onChange={(_, data) => setTransitive(data.checked)}
        label="Include nested groups"
        disabled={disabled}
      />
      <div className={styles.hint}>
        {transitive
          ? 'Including devices from groups nested inside this one.'
          : 'Only devices directly in this group. Toggle on to flatten nested groups.'}
      </div>

      <div className={styles.surface}>
        <DataTable
          columns={columns}
          data={devices}
          keyForRow={(d) => d.id}
          isLoading={query.isLoading}
          loadingRowCount={4}
          error={errorNode}
          selection={selection}
          emptyState={
            <span className={styles.hint}>No Intune-enrolled devices in this group.</span>
          }
        />
      </div>

      {!query.isError && devices.length > 0 && (
        <div className={styles.hint}>
          <span className="font-mono">{stagedInGroup}</span> of{' '}
          <span className="font-mono">{devices.length}</span> resolved device
          {devices.length === 1 ? '' : 's'} selected.
        </div>
      )}

      {query.hasNextPage && !query.isError && (
        <div className={styles.loadMoreRow}>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}

      {/* Zero-resolved + members skipped: a broad Autopilot dynamic group
          legitimately resolves few (or no) assignable devices. Guide the
          operator toward an enrolled/managed-scoped group instead of leaving
          them staring at an all-skipped result. */}
      {!query.isError && !query.isLoading && devices.length === 0 && unresolved.length > 0 && (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No assignable devices in this group</MessageBarTitle>
            None of this group’s members are Intune-enrolled, so none can be assigned. Broad
            Autopilot dynamic groups often contain mostly registered-but-not-enrolled devices —
            target a group scoped to enrolled or managed devices to bring devices in here.
          </MessageBarBody>
        </MessageBar>
      )}

      {unresolved.length > 0 && <UnresolvedSummary devices={unresolved} />}
    </div>
  );
}

// Members with no matching Intune managedDevice — Entra-registered but not
// enrolled (e.g. Autopilot placeholders), or stale. For a broad group these
// are EXPECTED, not errors: collapsed into one neutral zinc summary grouped by
// reason, expandable to the individual entries. Never staged, never hidden.
function UnresolvedSummary({ devices }: { devices: UnresolvedGroupDevice[] }) {
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo(() => {
    const byReason = new Map<string, UnresolvedGroupDevice[]>();
    for (const d of devices) {
      const reason = d.reason || 'Not assignable';
      const existing = byReason.get(reason);
      if (existing) existing.push(d);
      else byReason.set(reason, [d]);
    }
    return [...byReason.entries()];
  }, [devices]);

  const total = devices.length;
  const noun = `member${total === 1 ? '' : 's'}`;
  const summary =
    groups.length === 1
      ? `${total} group ${noun} skipped — ${groups[0][0]}`
      : `${total} group ${noun} skipped — across ${groups.length} reasons`;

  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-zinc-100"
      >
        <Info size={14} strokeWidth={1.5} className="shrink-0 text-zinc-400" />
        <span className="flex-1 text-xs font-medium text-zinc-700">{summary}</span>
        {expanded ? (
          <ChevronDown size={14} strokeWidth={1.5} className="shrink-0 text-zinc-400" />
        ) : (
          <ChevronRight size={14} strokeWidth={1.5} className="shrink-0 text-zinc-400" />
        )}
      </button>
      {expanded && (
        <div className="max-h-48 overflow-y-auto border-t border-zinc-200 px-3 py-2">
          {groups.map(([reason, list]) => (
            <div key={reason} className="mb-3 last:mb-0">
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                {reason} ({list.length})
              </div>
              <ul className="flex flex-col gap-1.5">
                {list.map((d, i) => (
                  <li
                    key={d.entraDeviceId ?? `${d.entraDeviceDisplayName}-${i}`}
                    className="flex flex-col"
                  >
                    <span className="text-sm text-zinc-700">
                      {d.entraDeviceDisplayName ?? '—'}
                    </span>
                    <span className="font-mono text-[11px] text-zinc-400">
                      {d.entraDeviceId ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Non-consent error rendering, shared by the group search and device expansion.
// 502 GRAPH_ERROR is transient (retry, or fall back to lookup/paste tabs);
// 404 GROUP_NOT_FOUND is definitive; D2C_HAS_NO_SOURCE_TENANT should not occur
// (the tab is T2T-gated) but is handled defensively.
function GroupErrorBar({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const code = sourceGroupErrorCode(error);
  const transient = code === 'GRAPH_ERROR' || isGraphError(error);

  let title: string;
  let body: string;
  if (code === 'GROUP_NOT_FOUND') {
    title = 'Group not found';
    body =
      'This group no longer exists in the source tenant. Go back and pick another group.';
  } else if (code === 'D2C_HAS_NO_SOURCE_TENANT') {
    title = 'No source tenant';
    body = 'This migration has no source tenant to search. Use the Paste serials tab instead.';
  } else if (transient) {
    title = 'Source tenant temporarily unavailable';
    body =
      'Microsoft Graph did not respond. This is usually transient — retry, or use another tab.';
  } else {
    title = 'Could not reach the source tenant';
    body = error instanceof Error ? error.message : 'Unexpected error';
  }

  return (
    <MessageBar intent={transient ? 'warning' : 'error'}>
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {body}
      </MessageBarBody>
      {transient && (
        <Button
          size="small"
          appearance="subtle"
          icon={<RefreshCw size={14} strokeWidth={1.5} />}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
    </MessageBar>
  );
}
